import { AuthorizationError, Collaboration, CollaboratorError, ContainersPermission, InvalidInputError } from "@back4app2/sdk";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import TextInput from "../FormInputField/TextInput/TextInput";
import { DsButton, SettingsSection } from "../ds";
import back4app2 from "../../back4app2";
import CollaboratorPermissionModal, { ContainersPermissionInput } from "./CollaboratorPermissionModal";
import { CollaboratorsList } from "./CollaboratorsList";
import { RemoveCollaboratorModal } from "./RemoveCollaboratorModal";
import { ReactComponent as StatusSuccessSVG } from '../../assets/images/status-success.svg';
import LoadingSpinner from "../LoadingSpinner";
import { User } from "@back4app2/sdk";
import { BACK4APP_DOT_COM_DASHBOARD_URL } from "../../settings";

export interface CollaboratorsProps {
  appId: string;
  isOwner: boolean;
  currentUser: User;
}
const validateEmail = (email: string): string => {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return 'Please enter a valid email address';
  }
  
  return '';
}; 

const defaultFeaturesPermissions: ContainersPermissionInput = {
  Overview: 'Read',
  Logs: 'Read',
  Deployments: 'Read',
  Metrics: 'Read',
  Settings: {
    General: 'Read',
    BuildAndDeploy: 'Write',
    EnvVars: 'Write',
    Domain: 'Write',
    HealthAndAlert: 'Write',
  },
  Actions: {
    Deploy: 'Write',
  },
};

const Collaborators = (props: CollaboratorsProps) => {
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('');
  const [disableAddButton, setDisableAddButton] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [showInvite, setShowInvite] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [customPermissions] = useState<ContainersPermissionInput | undefined>(undefined);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaboration[]>([]);
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [collaboratorToRemove, setCollaboratorToRemove] = useState<Collaboration | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [collaboratorToEdit, setCollaboratorToEdit] = useState<Collaboration | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState<string>('');

  useEffect(() => {
    setIsLoadingCollaborators(true);
    
    const subscription = back4app2.subscribeToCollaborators(
      props.appId,
      (error, snapshot) => {
        if (error) {
          console.error('Failed to fetch collaborators:', error);
          setIsLoadingCollaborators(false);
        } else if (snapshot) {
          setCollaborators(snapshot);
          setIsLoadingCollaborators(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [props.appId]);
  
  const handleAddCollaborator = async () => {
    setDisableAddButton(true);
    setEmailError('');
    setShowInvite(false);
    const trimmedEmail = newCollaboratorEmail.trim();
    const error = validateEmail(trimmedEmail);
    
    if (error) {
      setEmailError(error);
      return;
    }

    try {
      const emailValid = await back4app2.validateEmail(trimmedEmail);
      setIsEmailValid(emailValid);
      if (!emailValid) {
        setShowInvite(true);
        return;
      } else {
        // Email is valid, show permission modal directly
        setShowPermissionModal(true);
      }
    } catch (error) {
      if (error instanceof InvalidInputError) {
        setEmailError(error.message);
        return;
      }
      setEmailError('Something went wrong!');
      return;
    } finally {
      setDisableAddButton(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewCollaboratorEmail(value);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
    if (showInvite) {
      setShowInvite(false);
    }
    if (disableAddButton) {
      setDisableAddButton(false);
    }
  };

  const convertToContainersPermissionInput = (permissions: ContainersPermission): ContainersPermissionInput => {
    return {
      Overview: permissions.Overview,
      Logs: permissions.Logs,
      Deployments: permissions.Deployments,
      Metrics: permissions.Metrics,
      Settings: {
        General: permissions.Settings.General,
        BuildAndDeploy: permissions.Settings.BuildAndDeploy as 'None' | 'Read' | 'Write',
        EnvVars: permissions.Settings.EnvVars as 'None' | 'Read' | 'Write',
        Domain: permissions.Settings.Domain as 'None' | 'Read' | 'Write',
        HealthAndAlert: permissions.Settings.HealthAndAlert as 'None' | 'Read' | 'Write',
      },
      Actions: {
        Deploy: permissions.Actions.Deploy,
      },
    };
  };

  const handleEditCollaborator = (collaborator: Collaboration) => {
    setCollaboratorToEdit(collaborator);
    setEditError('');
    setShowEditModal(true);
  }

  const handleSaveEdit = async (permissions: ContainersPermissionInput) => {
    if (!collaboratorToEdit) return;

    setIsEditing(true);
    setEditError('');

    try {
      await back4app2.updateCollaboratorPermission(
        props.appId,
        collaboratorToEdit.email,
        permissions as ContainersPermission
      );

      // List will be updated automatically via subscription

      // Show success message
      toast.success('Collaborator permissions updated successfully!', {
        className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />
      });

      // Close modal
      setShowEditModal(false);
      setCollaboratorToEdit(null);
    } catch (error) {
      if (error instanceof CollaboratorError || error instanceof AuthorizationError) {
        setEditError(error.message);
      } else {
        setEditError('Failed to update collaborator permissions. Please try again.');
      }
    } finally {
      setIsEditing(false);
    }
  }

  const handleCloseEditModal = () => {
    if (!isEditing) {
      setShowEditModal(false);
      setCollaboratorToEdit(null);
      setEditError('');
    }
  }

  const handleRemoveCollaborator = (collaborator: Collaboration) => {
    setCollaboratorToRemove(collaborator);
    setRemoveError('');
    setShowRemoveModal(true);
  }

  const handleConfirmRemove = async () => {
    if (!collaboratorToRemove) return;

    setIsRemoving(true);
    setRemoveError('');

    try {
      await back4app2.removeCollaborator(props.appId, collaboratorToRemove.email);
      
      // Show success message
      toast.success('Collaborator removed successfully!', {
        className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />
      });

      if (collaboratorToRemove.email === props.currentUser.username) {
        window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`;
      }
      
      // Close modal
      setShowRemoveModal(false);
      setCollaboratorToRemove(null);
    } catch (error) {
      if (error instanceof CollaboratorError || error instanceof AuthorizationError) {
        setRemoveError(error.message);
      } else {
        setRemoveError('Failed to remove collaborator. Please try again.');
      }
    } finally {
      setIsRemoving(false);
    }
  }

  const handleCloseRemoveModal = () => {
    if (!isRemoving) {
      setShowRemoveModal(false);
      setCollaboratorToRemove(null);
      setRemoveError('');
    }
  }

  const handleSave = async (permissions: ContainersPermissionInput) => {
    setIsFormSubmitting(true);

    try {
      if (isEmailValid) {
        // Email is valid, add collaborator directly
        await back4app2.addCollaborator(props.appId, newCollaboratorEmail, permissions as ContainersPermission);
      } else {
        // Email is not valid, send invite
        await back4app2.sendInviteToCollaborator(props.appId, newCollaboratorEmail, permissions as ContainersPermission);
      }

      // List will be updated automatically via subscription

      // Show success message
      const successMessage = isEmailValid 
        ? 'Collaborator added successfully!'
        : 'Invitation sent successfully!';
      toast.success(successMessage, {
        className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />
      });
    } catch (error) {
      if (error instanceof CollaboratorError) {
        setEmailError(error.message);
      }
    } finally {
      setIsFormSubmitting(false);
      setShowPermissionModal(false);
      setNewCollaboratorEmail('');
      setShowInvite(false);
      setIsEmailValid(false);
    }
  }
  
  // The owner is not in `collaborators` — the API returns collaborations, not
  // memberships — so the row can only be drawn for the viewer who is the owner.
  const peopleCount = collaborators.length + (props.isOwner ? 1 : 0);

  return (
    <SettingsSection
      eyebrow="collaborators"
      meta={peopleCount ? `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}` : undefined}
    >
      <div className="flex flex-col gap-3">
        <CollaboratorsList
          collaborators={collaborators}
          isAppOwner={props.isOwner}
          onRemove={handleRemoveCollaborator}
          onEdit={handleEditCollaborator}
          currentUserEmail={props.currentUser.username}
          ownerEmail={props.isOwner ? props.currentUser.username : undefined}
        />

        {isLoadingCollaborators ? (
          <div className="flex items-center gap-2 text-ds-115 text-navy-300">
            <LoadingSpinner width='1rem' height='1rem' /> Loading collaborators…
          </div>
        ) : null}

        {props.isOwner ? (<>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <TextInput
                type="email"
                value={newCollaboratorEmail}
                onChange={handleEmailChange}
                placeholderText="collaborator@company.com"
                isDisabled={!props.isOwner}
              />
            </div>
            <DsButton onClick={handleAddCollaborator} isDisabled={disableAddButton}>
              Invite
            </DsButton>
          </div>

          {emailError ? (
            <span className="text-ds-115 text-state-err">{emailError}</span>
          ) : null}

          {showInvite ? (
            <div className="flex flex-wrap items-center gap-2.5 rounded-control border border-state-warn/50 bg-navy-700 px-3 py-2.5">
              <span className="text-ds-115 text-navy-100">
                That address has no Back4app account yet.
              </span>
              <DsButton
                size="md"
                onClick={() => {
                  setIsEmailValid(false);
                  setShowPermissionModal(true);
                }}
              >
                Send an invite
              </DsButton>
            </div>
          ) : null}

          <span className="text-ds-115 leading-[1.5] text-navy-300">
            Collaborators can do everything except delete or suspend the app.
          </span>
        </>) : null}
      </div>

      <CollaboratorPermissionModal
        open={showPermissionModal}
        email={newCollaboratorEmail}
        onClose={() => {
          setShowPermissionModal(false);
          setIsEmailValid(false);
        }}
        onConfirm={handleSave}
        defaultPermissions={defaultFeaturesPermissions}
        customPermissions={customPermissions}
        isSubmitting={isFormSubmitting}
      />
      <CollaboratorPermissionModal
        open={showEditModal}
        email={collaboratorToEdit?.email || ''}
        onClose={handleCloseEditModal}
        onConfirm={handleSaveEdit}
        defaultPermissions={defaultFeaturesPermissions}
        customPermissions={collaboratorToEdit ? convertToContainersPermissionInput(collaboratorToEdit.permissions) : undefined}
        error={editError}
        isSubmitting={isEditing}
      />
      <RemoveCollaboratorModal
        open={showRemoveModal}
        collaborator={collaboratorToRemove}
        isRemoving={isRemoving}
        error={removeError}
        onClose={handleCloseRemoveModal}
        onConfirm={handleConfirmRemove}
      />
    </SettingsSection>
  )
}

export default Collaborators